import React, { useContext } from "react"
import { useWatch } from "react-hook-form"

import classNames from "classnames"

import { exists } from "shared/lib/exists"

import { InputContext } from "../../Input"

interface ReadonlyTextProps
  extends Omit<
    React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
    "value"
  > {
  value?: string | null
  render?: (
    value: string,
    args: { unit?: string | undefined }
  ) => React.JSX.Element
  showUnit?: boolean
  showAlways?: boolean
}

export const ReadonlyText = React.memo((props: ReadonlyTextProps) => {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormReadonly {...props} />
  }

  return <Readonly {...props} />
})

export const HookFormReadonly = React.memo((props: ReadonlyTextProps) => {
  const { name } = useContext(InputContext)
  const value = useWatch({ name }) ?? ""

  return <Readonly value={value} {...props} />
})

export const Readonly = React.memo(
  ({
    className,
    children,
    render,
    showUnit,
    value = "-",
    showAlways,
    ...props
  }: ReadonlyTextProps) => {
    const { readonly, loading, unit } = useContext(InputContext)
    const shouldShowUnit = unit && exists(value) && value !== "-"

    if ((!readonly && !showAlways) || loading) {
      return null
    }

    if (children) {
      return children as React.JSX.Element
    }

    if (typeof value !== "string") {
      return null
    }

    if (render) {
      return render(value, { unit })
    }

    const displayValue = shouldShowUnit ? `${value} ${unit}` : value

    return (
      <div
        {...props}
        className={classNames(
          "input__value",
          "input__value--readonly",
          "readonly",
          className
        )}
      >
        {displayValue}
      </div>
    )
  }
)
